/**
 * MobileProductCarousel — 3D carousel for product display on mobile during voice calls.
 *
 * Floats above the minimized voice bar (appended to document.body).
 * Uses CSS 3D perspective transforms for a rotating wheel effect.
 * Supports swipe/drag navigation, quantity/weight steppers, out-of-stock states.
 *
 * Options:
 *   primaryColor: hex string (e.g. '#3b82f6')
 *   onProductAdd(product): fired when user taps Add
 *   onClose(): fired when user taps close button
 */
import { hasRealVariants } from './variantUtils.js';
import { VariationCarouselPicker } from './VariationCarouselPicker.js';

export class MobileProductCarousel {
  constructor(options = {}) {
    this.onProductAdd = options.onProductAdd || (() => {});
    this.onAddDisabled = options.onAddDisabled || (() => false);
    this.onClose = options.onClose || (() => {});
    this.primaryColor = options.primaryColor || '#3b82f6';

    this.products = [];
    this.current = 0;
    this.amounts = [];
    this.sellByModes = [];

    this.container = null;
    this.wheel = null;
    this.dotsEl = null;
    this.currentPicker = null;

    this.anglePerCard = 42;
    this.radius = 240;

    this.dragging = false;
    this.startX = 0;
    this.swiped = false;
    this.dragThreshold = 40;

    this._boundTouchMove = this._onTouchMove.bind(this);
    this._boundTouchEnd = this._onTouchEnd.bind(this);
    this._boundMouseMove = this._onMouseMove.bind(this);
    this._boundMouseUp = this._onMouseUp.bind(this);
  }

  // ═══════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════

  show(products, title) {
    this.hide();
    if (!products || products.length === 0) return;

    this._normalizeProducts(products);
    this.current = 0;

    this._injectStyles();
    this._buildDOM(title);
    this._buildCards();
    this._buildDots();
    this._setupDragEvents();
    this._updateWheel();

    document.body.appendChild(this.container);

    this._positionAboveMobileBar();
  }

  hide() {
    if (this.currentPicker) {
      this.currentPicker.hide();
      this.currentPicker = null;
    }
    this._removeEventListeners();
    if (this.container && this.container.parentNode) {
      this.container.classList.add('ttp-carousel--hiding');
      const c = this.container;
      setTimeout(() => {
        if (c.parentNode) c.parentNode.removeChild(c);
      }, 280);
    }
    this.container = null;
    this.wheel = null;
    this.dotsEl = null;
  }

  destroy() {
    if (this.currentPicker) {
      this.currentPicker.destroy();
      this.currentPicker = null;
    }
    this._removeEventListeners();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    const toast = document.getElementById('ttpCarouselToast');
    if (toast) toast.remove();
    const style = document.getElementById('ttp-mobile-carousel-styles');
    if (style) style.remove();
  }

  showToast(productName, cartInfo) {
    const existing = document.getElementById('ttpCarouselToast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'ttpCarouselToast';
    toast.className = 'ttp-carousel-toast';

    const cartLine = cartInfo
      ? `<div class="ttp-carousel-toast__cart">${cartInfo.itemCount} item${cartInfo.itemCount !== 1 ? 's' : ''} · ${cartInfo.currency}${cartInfo.total.toFixed(2)}</div>`
      : '';

    toast.innerHTML = `
      <div class="ttp-carousel-toast__icon">✓</div>
      <div class="ttp-carousel-toast__text">
        <strong>${productName}</strong> added to cart
        ${cartLine}
      </div>
    `;
    toast.style.setProperty('--ttp-carousel-primary', this.primaryColor);
    document.body.appendChild(toast);

    const mobileBar = document.getElementById('mobile-voice-call-bar-container');
    const cartPill = document.getElementById('ttpMobileCartSummary');
    if (cartPill) {
      toast.style.bottom = `${parseInt(cartPill.style.bottom) + cartPill.offsetHeight + 8}px`;
    } else if (mobileBar) {
      toast.style.bottom = `${mobileBar.offsetHeight + 12}px`;
    } else {
      toast.style.bottom = '100px';
    }

    setTimeout(() => {
      toast.classList.add('ttp-carousel-toast--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }


  // ═══════════════════════════════════════════
  // DOM CONSTRUCTION
  // ═══════════════════════════════════════════

  _normalizeProducts(products) {
    this.products = products.map(p => ({
      ...p,
      imageUrl: p.imageUrl || p.image_url || '',
      inStock: p.inStock !== undefined ? p.inStock : (p.in_stock !== undefined ? p.in_stock : true),
      sellBy: p.sellBy || p.sell_by || 'quantity',
      cartQuantity: p.cartQuantity || p.cart_quantity || 0,
      brand: p.brand || '',
      unit: p.unit || '',
      currency: p.currency || 'USD',
    }));

    this.amounts = this.products.map(p => {
      const min = p.sellBy === 'weight' ? 0.5 : 1;
      return p.cartQuantity > 0 ? p.cartQuantity : min;
    });

    this.sellByModes = this.products.map(p => p.sellBy);
  }

  _buildDOM(title) {
    this.container = document.createElement('div');
    this.container.className = 'ttp-mobile-carousel';
    this.container.id = 'ttpMobileCarousel';
    this.container.style.setProperty('--ttp-carousel-primary', this.primaryColor);
    this.container.style.setProperty('--ttp-carousel-primary-light', this._hexToRgba(this.primaryColor, 0.12));
    this.container.style.setProperty('--ttp-carousel-primary-dark', this._darkenHex(this.primaryColor, 30));
    this.container.style.setProperty('--ttp-carousel-primary-shadow', this._hexToRgba(this.primaryColor, 0.3));

    const header = document.createElement('div');
    header.className = 'ttp-carousel-header';
    header.innerHTML = `
      <span class="ttp-carousel-title">${title || 'Products'}</span>
      <button class="ttp-carousel-close" aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `;
    header.querySelector('.ttp-carousel-close').addEventListener('click', () => {
      this.hide();
      this.onClose();
    });

    const area = document.createElement('div');
    area.className = 'ttp-carousel-area';

    this.wheel = document.createElement('div');
    this.wheel.className = 'ttp-carousel-wheel';
    area.appendChild(this.wheel);

    this.dotsEl = document.createElement('div');
    this.dotsEl.className = 'ttp-carousel-dots';

    this.container.appendChild(header);
    this.container.appendChild(area);
    this.container.appendChild(this.dotsEl);
  }

  _buildCards() {
    this.products.forEach((p, i) => {
      const card = document.createElement('div');
      card.className = 'ttp-carousel-card' + (!p.inStock ? ' ttp-carousel-card--oos' : '');
      card.dataset.index = i;

      const sym = p.currency === 'ILS' ? '₪' : '$';
      const sellBy = this.sellByModes[i];
      const displayVal = sellBy === 'weight'
        ? `${this.amounts[i].toFixed(1)} kg`
        : `${this.amounts[i]}`;
      const inCart = p.cartQuantity > 0;
      const needsPicker = hasRealVariants(p);

      card.innerHTML = `
        <div class="ttp-carousel-card__image">
          <img src="${p.imageUrl}" alt="${p.name}" onerror="this.style.display='none'" />
          ${!p.inStock ? '<div class="ttp-carousel-card__badge">Out of Stock</div>' : ''}
        </div>
        <div class="ttp-carousel-card__info">
          ${p.brand ? `<div class="ttp-carousel-card__brand">${p.brand}</div>` : ''}
          <div class="ttp-carousel-card__name">${p.name}</div>
          ${p.unit ? `<div class="ttp-carousel-card__unit">${p.unit}</div>` : ''}
          <div class="ttp-carousel-card__price">${sym}${p.price.toFixed(2)}</div>
        </div>
        <div class="ttp-carousel-card__actions">
          <div class="ttp-carousel-card__stepper">
            <button class="ttp-carousel-card__qty-btn ttp-carousel-minus" data-idx="${i}" ${!p.inStock ? 'disabled' : ''}>−</button>
            <span class="ttp-carousel-card__qty-val" id="ttpCQty${i}">${displayVal}</span>
            <button class="ttp-carousel-card__qty-btn ttp-carousel-plus" data-idx="${i}" ${!p.inStock ? 'disabled' : ''}>+</button>
          </div>
          <button class="ttp-carousel-card__add-btn${needsPicker ? ' ttp-carousel-card__add-btn--choose' : ''}${inCart ? ' ttp-carousel-card__add-btn--update' : ''}" data-idx="${i}" ${!p.inStock ? 'disabled' : ''}>
            ${p.inStock ? (needsPicker ? 'Choose Options' : (inCart ? 'Update' : 'Add to Cart')) : 'Unavailable'}
          </button>
        </div>
      `;

      card.style.transform = `rotateY(${i * this.anglePerCard}deg) translateZ(${this.radius}px)`;
      this.wheel.appendChild(card);
    });

    this._setupCardHandlers();
  }

  /**
   * Area-level tap detection — bypasses browser 3D hit-testing entirely.
   *
   * Instead of attaching handlers to individual buttons (which requires
   * the browser to correctly identify which 3D-transformed button was
   * tapped), we capture ALL touches at the carousel-area level and
   * manually match the touch coordinates against the current card's
   * buttons using getBoundingClientRect.
   */
  _setupCardHandlers() {
    const area = this.container.querySelector('.ttp-carousel-area');
    if (!area) return;

    let tapOrigin = null;

    area.addEventListener('touchstart', (e) => {
      tapOrigin = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, { passive: true });

    area.addEventListener('touchend', (e) => {
      if (!tapOrigin || this.swiped) {
        tapOrigin = null;
        return;
      }

      const touch = e.changedTouches[0];
      const dx = Math.abs(touch.clientX - tapOrigin.x);
      const dy = Math.abs(touch.clientY - tapOrigin.y);
      tapOrigin = null;

      if (dx > 15 || dy > 15) return;

      e.preventDefault();
      this._handleTapAt(touch.clientX, touch.clientY);
    });

    area.addEventListener('click', (e) => {
      const target = e.target;
      const card = target.closest('.ttp-carousel-card');
      if (!card || parseInt(card.dataset.index) !== this.current) return;

      const minus = target.closest('.ttp-carousel-minus');
      const plus = target.closest('.ttp-carousel-plus');
      const addBtn = target.closest('.ttp-carousel-card__add-btn');

      if (minus && !minus.disabled) {
        e.stopPropagation();
        this._handleMinus();
      } else if (plus && !plus.disabled) {
        e.stopPropagation();
        this._handlePlus();
      } else if (addBtn && !addBtn.disabled) {
        e.stopPropagation();
        this._handleAdd();
      }
    });
  }

  _handleTapAt(x, y) {
    const cards = this.wheel.querySelectorAll('.ttp-carousel-card');
    const currentCard = cards[this.current];
    if (!currentCard) return;

    const minus = currentCard.querySelector('.ttp-carousel-minus');
    const plus = currentCard.querySelector('.ttp-carousel-plus');
    const addBtn = currentCard.querySelector('.ttp-carousel-card__add-btn');

    if (this._hitTest(x, y, minus) && !minus?.disabled) {
      this._handleMinus();
    } else if (this._hitTest(x, y, plus) && !plus?.disabled) {
      this._handlePlus();
    } else if (this._hitTest(x, y, addBtn) && !addBtn?.disabled) {
      this._handleAdd();
    }
  }

  _hitTest(x, y, el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  _handleMinus() {
    const idx = this.current;
    const sellBy = this.sellByModes[idx];
    const min = sellBy === 'weight' ? 0.5 : 1;
    const step = sellBy === 'weight' ? 0.5 : 1;
    if (this.amounts[idx] > min) {
      this.amounts[idx] = Math.round((this.amounts[idx] - step) * 10) / 10;
      this._refreshStepper(idx);
    }
  }

  _handlePlus() {
    const idx = this.current;
    const step = this.sellByModes[idx] === 'weight' ? 0.5 : 1;
    this.amounts[idx] = Math.round((this.amounts[idx] + step) * 10) / 10;
    this._refreshStepper(idx);
  }

  _handleAdd() {
    const idx = this.current;
    const product = this.products[idx];
    if (!product.inStock || this.onAddDisabled()) return;

    if (hasRealVariants(product)) {
      const picker = new VariationCarouselPicker({
        primaryColor: this.primaryColor,
        onProductAdd: (cardEl, productData) => {
          this.onProductAdd(cardEl, productData);
          setTimeout(() => this.hide(), 800);
        },
        onClose: () => {
          this.currentPicker = null;
          if (this.container) this.container.classList.remove('ttp-carousel--picker-open');
        },
      });
      this.currentPicker = picker;
      if (this.container) this.container.classList.add('ttp-carousel--picker-open');
      picker.show(product);
      return;
    }

    const cards = this.wheel.querySelectorAll('.ttp-carousel-card');
    const cardEl = cards[idx];
    const btn = cardEl?.querySelector('.ttp-carousel-card__add-btn');
    if (btn) {
      btn.textContent = '✓ Added!';
      btn.classList.add('ttp-carousel-card__add-btn--added');
      btn.disabled = true;
    }

    const productData = {
      ...product,
      selectedAmount: this.amounts[idx],
      sellBy: this.sellByModes[idx],
    };

    this.onProductAdd(cardEl, productData);

    setTimeout(() => this.hide(), 800);
  }

  _refreshStepper(idx) {
    const sellBy = this.sellByModes[idx];
    const min = sellBy === 'weight' ? 0.5 : 1;
    const el = this.container.querySelector(`#ttpCQty${idx}`);
    const minusBtn = this.container.querySelector(`.ttp-carousel-minus[data-idx="${idx}"]`);
    if (el) {
      el.textContent = sellBy === 'weight'
        ? `${this.amounts[idx].toFixed(1)} kg`
        : `${this.amounts[idx]}`;
    }
    if (minusBtn) {
      minusBtn.disabled = this.amounts[idx] <= min;
    }
  }

  _buildDots() {
    if (this.products.length <= 1) return;
    this.products.forEach((_, i) => {
      const dot = document.createElement('div');
      dot.className = 'ttp-carousel-dot';
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsEl.appendChild(dot);
    });
  }

  // ═══════════════════════════════════════════
  // 3D WHEEL
  // ═══════════════════════════════════════════

  _updateWheel() {
    const rotation = -(this.current * this.anglePerCard);
    this.wheel.style.transform = `translateZ(-${this.radius}px) rotateY(${rotation}deg)`;

    const cards = this.wheel.querySelectorAll('.ttp-carousel-card');
    cards.forEach((card, i) => {
      let eff = (i * this.anglePerCard) + rotation;
      while (eff > 180) eff -= 360;
      while (eff < -180) eff += 360;

      if (Math.abs(eff) > 90) {
        card.style.visibility = 'hidden';
        card.style.opacity = '0';
      } else {
        card.style.visibility = 'visible';
        card.style.opacity = this.products[i].inStock ? '1' : '0.7';
      }
    });

    const dots = this.dotsEl.querySelectorAll('.ttp-carousel-dot');
    dots.forEach((dot, i) => dot.classList.toggle('ttp-carousel-dot--active', i === this.current));
  }

  goTo(i) {
    this.current = Math.max(0, Math.min(this.products.length - 1, i));
    this._updateWheel();
  }

  // ═══════════════════════════════════════════
  // DRAG / SWIPE
  // ═══════════════════════════════════════════

  _setupDragEvents() {
    const area = this.container.querySelector('.ttp-carousel-area');
    if (!area) return;

    area.addEventListener('touchstart', (e) => {
      this.dragging = true;
      this.startX = e.touches[0].clientX;
      this.swiped = false;
    }, { passive: true });

    area.addEventListener('mousedown', (e) => {
      this.dragging = true;
      this.startX = e.clientX;
      this.swiped = false;
    });

    document.addEventListener('touchmove', this._boundTouchMove, { passive: true });
    document.addEventListener('touchend', this._boundTouchEnd);
    document.addEventListener('mousemove', this._boundMouseMove);
    document.addEventListener('mouseup', this._boundMouseUp);
  }

  _onTouchMove(e) {
    if (!this.dragging) return;
    const diff = e.touches[0].clientX - this.startX;
    if (Math.abs(diff) > this.dragThreshold) {
      this.goTo(this.current + (diff > 0 ? -1 : 1));
      this.startX = e.touches[0].clientX;
      this.swiped = true;
    }
  }

  _onTouchEnd() {
    this.dragging = false;
    if (this.swiped) {
      setTimeout(() => { this.swiped = false; }, 400);
    }
  }

  _onMouseMove(e) {
    if (!this.dragging) return;
    const diff = e.clientX - this.startX;
    if (Math.abs(diff) > this.dragThreshold) {
      this.goTo(this.current + (diff > 0 ? -1 : 1));
      this.startX = e.clientX;
      this.swiped = true;
    }
  }

  _onMouseUp() {
    this.dragging = false;
    if (this.swiped) {
      setTimeout(() => { this.swiped = false; }, 400);
    }
  }

  _removeEventListeners() {
    document.removeEventListener('touchmove', this._boundTouchMove);
    document.removeEventListener('touchend', this._boundTouchEnd);
    document.removeEventListener('mousemove', this._boundMouseMove);
    document.removeEventListener('mouseup', this._boundMouseUp);
  }

  // ═══════════════════════════════════════════
  // POSITIONING
  // ═══════════════════════════════════════════

  _positionAboveMobileBar() {
    requestAnimationFrame(() => {
      if (!this.container) return;
      const bar = document.getElementById('mobile-voice-call-bar-container');
      if (bar) {
        const barRect = bar.getBoundingClientRect();
        const widgetClearance = 50;
        this.container.style.bottom = `${window.innerHeight - barRect.top + 8 + widgetClearance}px`;
      }
    });
  }

  // ═══════════════════════════════════════════
  // COLOR HELPERS
  // ═══════════════════════════════════════════

  _hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  _darkenHex(hex, amount) {
    const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
    const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
    const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // ═══════════════════════════════════════════
  // STYLES
  // ═══════════════════════════════════════════

  _injectStyles() {
    if (document.getElementById('ttp-mobile-carousel-styles')) return;
    const s = document.createElement('style');
    s.id = 'ttp-mobile-carousel-styles';
    s.textContent = this._generateCSS();
    document.head.appendChild(s);
  }

  _generateCSS() {
    return `
/* ═══════════════════════════════════════
   MOBILE PRODUCT CAROUSEL
   ═══════════════════════════════════════ */

.ttp-mobile-carousel {
  position: fixed !important;
  bottom: 180px;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  z-index: 2147483646 !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  animation: ttpCarouselIn 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  -webkit-tap-highlight-color: transparent;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 16px;
}

.ttp-mobile-carousel > .ttp-carousel-header,
.ttp-mobile-carousel > .ttp-carousel-area,
.ttp-mobile-carousel > .ttp-carousel-dots {
  position: relative;
  z-index: 1;
}

.ttp-mobile-carousel::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 420px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: 24px 24px 0 0;
  pointer-events: none;
  z-index: 0;
}

.ttp-mobile-carousel.ttp-carousel--hiding {
  animation: ttpCarouselOut 0.28s ease forwards;
  pointer-events: none;
}

/* Hide carousel when variation picker is open so products are not visible behind it */
.ttp-mobile-carousel.ttp-carousel--picker-open {
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

@keyframes ttpCarouselIn {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ttpCarouselOut {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(30px); }
}

/* ── Header ── */

.ttp-carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px 8px;
  flex-shrink: 0;
}

.ttp-carousel-title {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0,0,0,0.4);
}

.ttp-carousel-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0;
  background: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  padding: 0;
  opacity: 0.85;
}
.ttp-carousel-close:active {
  opacity: 1;
}

/* ── 3D Carousel Area ── */

.ttp-carousel-area {
  position: relative;
  height: 300px;
  flex-shrink: 0;
  perspective: 800px;
  perspective-origin: 50% 55%;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}
.ttp-carousel-area:active {
  cursor: grabbing;
}

.ttp-carousel-wheel {
  position: absolute;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}

/* ── Card ── */

.ttp-carousel-card {
  position: absolute;
  width: 150px;
  height: 285px;
  left: 50%;
  top: 50%;
  margin-left: -75px;
  margin-top: -142px;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  transform-style: preserve-3d;
  transition: visibility 0.3s, opacity 0.3s;
  display: flex;
  flex-direction: column;
}

.ttp-carousel-card--oos {
  opacity: 0.7;
}

/* Card Image */
.ttp-carousel-card__image {
  width: 100%;
  height: 100px;
  background: linear-gradient(145deg, #f0f4ff, #e8ecf8);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.ttp-carousel-card__image img {
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
}

.ttp-carousel-card__badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #ef4444;
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Card Info */
.ttp-carousel-card__info {
  padding: 8px 10px 4px;
  flex-shrink: 0;
}

.ttp-carousel-card__brand {
  font-size: 9px;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 1px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ttp-carousel-card__name {
  font-size: 12px;
  font-weight: 600;
  color: #1e1b4b;
  line-height: 1.25;
  margin-bottom: 1px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ttp-carousel-card__unit {
  font-size: 9px;
  color: #64748b;
  margin-bottom: 3px;
}

.ttp-carousel-card__price {
  font-size: 16px;
  font-weight: 700;
  color: var(--ttp-carousel-primary, #3b82f6);
}

/* Card Actions */
.ttp-carousel-card__actions {
  padding: 6px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
}

/* Stepper */
.ttp-carousel-card__stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ttp-carousel-primary-light, rgba(59,130,246,0.12));
  border-radius: 10px;
  padding: 3px;
}

.ttp-carousel-card__qty-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: var(--ttp-carousel-primary, #3b82f6);
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  touch-action: manipulation;
}
.ttp-carousel-card__qty-btn:active:not(:disabled) {
  background: var(--ttp-carousel-primary-light, rgba(59,130,246,0.12));
}
.ttp-carousel-card__qty-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.ttp-carousel-card__qty-val {
  min-width: 40px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #1e1b4b;
  user-select: none;
}

/* Add Button */
.ttp-carousel-card__add-btn {
  width: 100%;
  padding: 9px 12px;
  background: linear-gradient(135deg, var(--ttp-carousel-primary, #3b82f6), var(--ttp-carousel-primary-dark, #2563eb));
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px var(--ttp-carousel-primary-shadow, rgba(59,130,246,0.3));
  transition: transform 0.15s, box-shadow 0.15s;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  touch-action: manipulation;
}
.ttp-carousel-card__add-btn:active:not(:disabled) {
  transform: scale(0.97);
}
.ttp-carousel-card__add-btn:disabled {
  background: #94a3b8;
  cursor: default;
  box-shadow: none;
}

.ttp-carousel-card__add-btn--added {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
}

.ttp-carousel-card__add-btn--update {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.ttp-carousel-card__add-btn--choose {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.ttp-products-animating .ttp-carousel-card__add-btn {
  opacity: 0.4;
  pointer-events: none;
  cursor: not-allowed;
}

/* ── Dots ── */

.ttp-carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 8px 0 0;
  flex-shrink: 0;
}

.ttp-carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.35);
  cursor: pointer;
  transition: all 0.3s;
  -webkit-tap-highlight-color: transparent;
}

.ttp-carousel-dot--active {
  width: 24px;
  border-radius: 4px;
  background: #fff;
}

/* ── Toast ── */

.ttp-carousel-toast {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 100px;
  z-index: 100001;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  animation: ttpCarouselToastIn 0.3s ease;
  pointer-events: auto;
}

.ttp-carousel-toast--hiding {
  animation: ttpCarouselOut 0.3s ease forwards;
}

@keyframes ttpCarouselToastIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

.ttp-carousel-toast__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.ttp-carousel-toast__text {
  font-size: 13px;
  color: #1e293b;
  line-height: 1.3;
}
.ttp-carousel-toast__text strong {
  font-weight: 600;
}
.ttp-carousel-toast__cart {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
  font-weight: 500;
}
`;
  }
}
